<template>
  <div class="min-h-screen bg-background-light">
    <template v-if="!auth.isAuthenticated">
      <header class="fixed top-0 left-0 right-0 z-50 bg-white border-b border-primary/10">
        <div class="max-w-7xl mx-auto px-6 lg:px-20 h-16 flex items-center justify-between">
          <RouterLink class="flex items-center gap-3" to="/">
            <div class="size-10 bg-primary flex items-center justify-center rounded-xl text-white">
              <Icon name="logo" class="size-6" />
            </div>
            <div class="leading-tight">
              <div class="text-primary text-sm font-black tracking-tight uppercase">Guépard Express</div>
              <div class="text-slate-500 text-xs font-semibold">Plateforme client</div>
            </div>
          </RouterLink>
          <RouterLink
            class="h-10 px-4 rounded-lg bg-primary text-white font-bold flex items-center shadow-lg shadow-primary/20"
            to="/login"
            >Connexion</RouterLink
          >
        </div>
      </header>

      <div class="pt-16">
        <div class="max-w-7xl mx-auto px-6 lg:px-20 py-8">
          <div class="rounded-2xl bg-white border border-primary/10 p-10">
            <div class="max-w-xl">
              <h1 class="text-3xl font-black tracking-tight text-slate-900">Accès à votre espace</h1>
              <p class="mt-3 text-slate-600 font-medium">
                Connectez-vous pour créer des expéditions, comparer les transporteurs, imprimer des étiquettes et suivre vos colis.
              </p>
              <div class="mt-6 flex gap-3">
                <RouterLink class="h-12 px-6 rounded-xl bg-primary text-white font-bold flex items-center" to="/login"
                  >Se connecter</RouterLink
                >
                <RouterLink class="h-12 px-6 rounded-xl border border-primary/20 text-primary font-bold flex items-center" to="/"
                  >Retour au site</RouterLink
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="font-sans">
        <!-- Top Navigation -->
        <nav class="fixed top-0 left-0 right-0 z-50 glass-card border-b border-slate-200/50">
          <div class="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
            <RouterLink to="/app/dashboard" class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shadow-lg shadow-brand-500/30 animate-float text-white">
                <Icon name="logo" class="w-6 h-6" />
              </div>
              <div>
                <h1 class="text-lg font-bold text-dark-900 tracking-tight">GUÉPARD EXPRESS</h1>
                <p class="text-[10px] text-dark-400 font-medium uppercase tracking-widest">
                  {{ auth.isAdmin ? 'Console administrateur' : 'Plateforme client' }}
                </p>
              </div>
            </RouterLink>

            <div class="flex items-center gap-4">
              <button class="relative p-2 rounded-xl hover:bg-slate-100 transition-colors" type="button" title="Notifications">
                <Icon name="bell" class="w-5 h-5 text-dark-500" />
              </button>

              <div class="h-8 w-px bg-slate-200"></div>

              <div class="flex items-center gap-3 px-3 py-1.5 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer">
                <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white text-sm font-bold shadow-md">
                  {{ userInitial }}
                </div>
                <div class="hidden sm:block">
                  <p class="text-sm font-semibold text-dark-800">{{ auth.user?.name || 'Client' }}</p>
                  <p class="text-[10px] text-dark-400">{{ auth.isAdmin ? 'Administrateur' : 'Client' }}</p>
                </div>
              </div>

              <button
                class="px-4 py-2 text-sm font-medium text-dark-600 hover:text-brand-700 hover:bg-brand-50 rounded-xl transition-all"
                type="button"
                @click="onLogout"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </nav>

        <!-- Sidebar (desktop) -->
        <aside class="hidden lg:block fixed left-0 top-16 bottom-0 w-64 bg-white/90 backdrop-blur-xl border-r border-slate-200/50 z-40 overflow-y-auto">
          <div class="p-5">
            <p class="text-[10px] font-bold text-dark-400 uppercase tracking-widest mb-4 px-3">Navigation</p>

            <nav class="space-y-1">
              <RouterLink class="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-dark-600 hover:text-brand-700 hover:bg-brand-50/50" to="/app/dashboard">
                <Icon name="grid" class="nav-icon w-5 h-5" />
                <span class="text-sm font-medium">Tableau de bord</span>
              </RouterLink>
              <RouterLink class="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-dark-600 hover:text-brand-700 hover:bg-brand-50/50" to="/app/shipments">
                <Icon name="box" class="nav-icon w-5 h-5" />
                <span class="text-sm font-medium">Expéditions</span>
              </RouterLink>
              <RouterLink class="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-dark-600 hover:text-brand-700 hover:bg-brand-50/50" to="/app/commandes">
                <Icon name="doc" class="nav-icon w-5 h-5" />
                <span class="text-sm font-medium">{{ auth.isAdmin ? 'Toutes les commandes' : 'Mes colis' }}</span>
              </RouterLink>
              <RouterLink v-if="auth.isAdmin" class="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-dark-600 hover:text-brand-700 hover:bg-brand-50/50" to="/admin/regroupements">
                <Icon name="users" class="nav-icon w-5 h-5" />
                <span class="text-sm font-medium">Demandes de regroupement</span>
              </RouterLink>
              <a class="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-dark-300 opacity-60 pointer-events-none" href="#">
                <Icon name="tag" class="nav-icon w-5 h-5" />
                <span class="text-sm font-medium">Tarifs</span>
              </a>
              <a class="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-dark-300 opacity-60 pointer-events-none" href="#">
                <Icon name="label" class="nav-icon w-5 h-5" />
                <span class="text-sm font-medium">Étiquettes</span>
              </a>
              <a class="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-dark-300 opacity-60 pointer-events-none" href="#">
                <Icon name="compass" class="nav-icon w-5 h-5" />
                <span class="text-sm font-medium">Suivi</span>
              </a>
              <a class="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-dark-300 opacity-60 pointer-events-none" href="#">
                <Icon name="gear" class="nav-icon w-5 h-5" />
                <span class="text-sm font-medium">Paramètres</span>
              </a>
            </nav>

            <!-- Aide -->
            <div class="mt-6 p-4 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 text-white relative overflow-hidden">
              <div class="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-6 translate-x-6"></div>
              <div class="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-4 -translate-x-4"></div>
              <div class="relative">
                <div class="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center mb-3">
                  <Icon name="info" class="w-4 h-4" />
                </div>
                <p class="text-sm font-bold mb-1">Besoin d'aide ?</p>
                <p class="text-[11px] text-white/80 mb-3">Notre équipe est disponible pour vous accompagner.</p>
                <button class="w-full py-1.5 bg-white text-brand-700 text-xs font-bold rounded-lg hover:bg-white/90 transition-colors" type="button">
                  Contacter le support
                </button>
              </div>
            </div>
          </div>
        </aside>

        <!-- Navigation mobile (sous le header, sidebar masquée en dessous de lg) -->
        <nav class="lg:hidden fixed top-16 left-0 right-0 z-40 bg-white border-b border-slate-200/60 overflow-x-auto">
          <div class="flex items-center gap-1 px-4 py-2 min-w-max">
            <RouterLink class="mobile-nav-item" to="/app/dashboard">Tableau de bord</RouterLink>
            <RouterLink class="mobile-nav-item" to="/app/shipments">Expéditions</RouterLink>
            <RouterLink class="mobile-nav-item" to="/app/commandes">{{ auth.isAdmin ? 'Commandes' : 'Mes colis' }}</RouterLink>
            <RouterLink v-if="auth.isAdmin" class="mobile-nav-item" to="/admin/regroupements">Regroupements</RouterLink>
          </div>
        </nav>

        <!-- Main Content -->
        <main class="lg:ml-64 pt-28 lg:pt-16 min-h-screen">
          <div class="max-w-[1400px] mx-auto p-6 sm:p-8">
            <RouterView v-slot="{ Component }">
              <Transition name="page" mode="out-in">
                <component :is="Component" />
              </Transition>
            </RouterView>
          </div>
        </main>
      </div>
    </template>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth'
import Icon from '@/components/ui/Icon.vue'

export default {
  name: 'AppShell',
  components: { Icon },
  computed: {
    auth() {
      return useAuthStore()
    },
    userInitial() {
      const name = this.auth.user?.name || 'C'
      return name.trim().charAt(0).toUpperCase()
    }
  },
  mounted() {
    // Rafraîchir les infos utilisateur au chargement si un token existe
    if (this.auth.token) {
      this.auth.fetchMe().catch(err => {
        console.error('Erreur rafraîchissement utilisateur:', err)
      })
    }
  },
  methods: {
    onLogout() {
      this.auth.logout()
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.sidebar-link {
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.sidebar-link::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 0;
  background: linear-gradient(180deg, #3b82f6, #1d4ed8);
  border-radius: 0 4px 4px 0;
  transition: height 0.3s ease;
}
.sidebar-link:hover::before,
.sidebar-link.router-link-active::before {
  height: 60%;
}
.sidebar-link.router-link-active {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05));
  color: #1d4ed8;
}
.nav-icon {
  transition: transform 0.3s ease;
}
.sidebar-link:hover .nav-icon {
  transform: scale(1.1);
}

.mobile-nav-item {
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  color: rgb(71 85 105);
  white-space: nowrap;
}
.mobile-nav-item.router-link-active {
  background: rgba(37, 99, 235, 0.1);
  color: #1d4ed8;
}

/* Transition entre les pages de l'espace connecté (/app, /admin) */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.page-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.page-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
